/* eslint-disable no-undef */
import { Card as CoherenceCard } from "@coherence-design-system/controls";
import { IBreadcrumbItem, IBreadcrumbProps, Icon } from "@fluentui/react";
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import {
  AccordionDetails,
  AccordionSummaryProps,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  Stack,
  Typography
} from "@mui/material";
import MuiAccordion, { AccordionProps } from "@mui/material/Accordion";
import MuiAccordionSummary, { accordionSummaryClasses } from "@mui/material/AccordionSummary";
import { styled, StyledEngineProvider } from "@mui/material/styles";
import CryptoJS from "crypto-js";
import { useNavigate } from "react-router-dom";

import { CustomBreadcrumb } from "../SharedComponents/CustomBreadcrumb";
import AIPoweredCaseSummaryDocument from "../assets/data-research/AIPoweredCaseSummary.docx";
import CESPurviewAnalysis from "../assets/data-research/CESPurviewAnalysis-DataAnalyticsAndResearch.docx";
import CFHProjectPlanDocument from "../assets/data-research/CFH_ProjectPlan.docx";
import CustomerEngagementAISummarization from "../assets/data-research/CustomerEngagementAISummarization.docx";
import PropensityModelsFramework from "../assets/data-research/PropensityModelsFramework.docx";
import SecureExchangeDashboardWorkflow from "../assets/data-research/SecureExchangeDashboardWorkflow.docx";
import { useEffect } from "react";
import { logPageView } from "../helper/AppInsightsHelper";

interface Document {
  description: string;
  fileType: "pdf" | "docx" | "pptx";
  name: string;
  url: string;
}

interface Category {
  category: string;
  filesList: Document[];
}

interface DocumentsData {
  files: Category[];
}

const cryptoEncryptionKey = process.env.REACT_APP_CRYPTOJS_ENCRYPTION_KEY!;

const encryptParams = (params: string) => {
  return CryptoJS.AES.encrypt(params, cryptoEncryptionKey).toString();
};

const filesCatalog: DocumentsData = {
  files: [
    // {
    //   category: "Forecasting",
    //   filesList: []
    // },
    {
      category: "Propensity",
      filesList: [
        {
          name: "CES Purview Analysis - Data Analytics & Research",
          url: CESPurviewAnalysis,
          description:
            "The CES Purview Analysis - Data Analytics & Research Document aims to develop a business intelligence-based approach to identify tenants who might churn by analyzing support cases, product usage, and subscription details. The document outlines the use of tools like Power BI, SSMS, and Kusto to create a comprehensive Power BI report that is refreshed bi-weekly.",
          fileType: "docx"
        },
        {
          name: "Propensity Models Framework",
          url: PropensityModelsFramework,
          description:
            "The Propensity Models Framework document outlines a strategy for identifying potential customers for Microsoft's new consumption-based offerings. It focuses on developing a framework to target customers with a higher propensity to adopt these offerings at different phases of the launch process",
          fileType: "docx"
        },
        {
          name: "SecureExchange - Dashboard Workflow",
          url: SecureExchangeDashboardWorkflow,
          description:
            "The SecureExchange: Data Research Document aims to identify tenants with a high propensity to opt for the secure exchange offering. This is based on various data points such as the tenant's Entra plan category, MBS data, and other associated details.",
          fileType: "docx"
        }
      ]
    },

    {
      category: "AI Generated Summaries",
      filesList: [
        {
          name: "AI Powered Case Summary",
          url: AIPoweredCaseSummaryDocument,
          description:
            "Shared Services is developing an AI-powered solution to enhance support ticket analysis by summarizing cases, recommending actions, identifying root causes, and predicting deflection potential.",
          fileType: "docx"
        },
        {
          name: "Customer Engagement - AI Summarization",
          url: CustomerEngagementAISummarization,
          description:
            "The document outlines the process and key points for summarizing customer engagement notes, including reasons for product adoption or non-adoption, blockers, and future business scope.",
          fileType: "docx"
        }
      ]
    },
    {
      category: "Anomaly Detection",
      filesList: [
        {
          name: "CFH Project Plan",
          url: CFHProjectPlanDocument,
          description:
            "This hub will capture product feedback, highlight service gaps, and identify underused features.",
          fileType: "docx"
        }
      ]
      // },
      // {
      //   category: "Scoring",
      //   filesList: []
    }
  ]
};

const style = {
  margin: 10,
  height: "calc(100vh - 68px)"
};

const breadcrumbStyles = {
  root: {
    fontSize: "14px",
    margin: 0,
    paddingLeft: "8px",
    "--itemLink-color": "rgb(0, 120, 212)"
  },
  item: {
    fontSize: "inherit"
  },
  itemLink: {
    fontSize: "inherit",
    color: "var(--itemLink-color)",
    ":hover": {
      background: "none",
      textDecoration: "underline",
      color: "var(--itemLink-color)"
    }
  }
};

const Accordion = styled((props: AccordionProps) => <MuiAccordion disableGutters elevation={0} square {...props} />)(
  ({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`
  })
);

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: ".9rem", color: "inherit" }} />}
    {...props}
  />
))(({ theme }) => ({
  backgroundColor: "rgba(0, 0, 0, .03)",
  flexDirection: "row-reverse",
  gap: 8,
  [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]: {
    transform: "rotate(90deg)"
  },
  [`& .${accordionSummaryClasses.content}`]: {
    marginLeft: theme.spacing(2)
  }
}));

export const DataResearchPage = () => {
  const navigate = useNavigate();
  const items: IBreadcrumbItem[] = [
    { text: "Home", key: "home", onClick: () => navigate("/") },
    { text: "Data Research", key: "dataResearch", isCurrentItem: true }
  ];

  const bcrops: IBreadcrumbProps = {
    items: items,
    styles: breadcrumbStyles
  };

  const handleItemClick = (document: Document) => {
    const encryptedParams = encryptParams(JSON.stringify(document));
    navigate(`/dataresearchview?id=${encodeURIComponent(encryptedParams)}`);
  };

  useEffect(() => {
    logPageView("Data Research Page", window.location.href);
  }, []);

  return (
    <div style={style}>
      <CoherenceCard
        header={<CustomBreadcrumb {...bcrops} />}
        styles={{ layout: { "> div": { overflow: "auto !important" } } }}
      >
        {filesCatalog.files.map((category) => (
          <Accordion defaultExpanded key={category.category}>
            <AccordionSummary>
              <Typography>{category.category}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={{ xs: 1, sm: 2, md: 3 }}>
                {category.filesList.map((pdf) => (
                  <Grid item xs={12} sm={6} md={4} lg={4} xl={3} key={crypto.randomUUID()}>
                    <StyledEngineProvider injectFirst>
                      <Card
                        sx={{
                          cursor: "pointer",
                          height: "100%"
                        }}
                      >
                        <CardActionArea onClick={() => handleItemClick(pdf)} sx={{ height: "100%" }}>
                          <CardContent sx={{ margin: "16px !important", height: "100%" }}>
                            <Stack direction="row" alignItems="center" justifyContent="flex-start" spacing={1}>
                              {(() => {
                                if (pdf.fileType === "pdf") {
                                  return <PictureAsPdfIcon color="error" />;
                                } else if (pdf.fileType === "pptx") {
                                  return (
                                    <Icon
                                      iconName="PowerPointDocument"
                                      styles={{ root: { color: "#d24726", fontSize: 20 } }}
                                    />
                                  );
                                } else {
                                  return (
                                    <Icon
                                      iconName="WordDocument"
                                      styles={{ root: { color: "#2b579a", fontSize: 20 } }}
                                    />
                                  );
                                }
                              })()}
                              <Typography gutterBottom variant="body1" component="div">
                                {pdf.name}
                              </Typography>
                            </Stack>
                            <Typography variant="subtitle2" color="text.secondary">
                              {pdf.description}
                            </Typography>
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    </StyledEngineProvider>
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>
        ))}
      </CoherenceCard>
    </div>
  );
};
